/** File writes folded by one completed run. Nothing is stored. */
import type { TranscriptMessage } from '@mod/core/model'
import { type FileChange, projectSessionChanges } from './sessionChanges'
import { startsTurn } from './turns'

export interface RunFileChanges {
  key: string
  /** The turn's transcript range, prompt first, `end` exclusive. The card is
   *  drawn after the last row this range created. */
  start: number
  end: number
  /** Prompts inside the range whose runs have not started. Their rows are
   *  not this turn's, so the card never follows one. */
  waiting: number[]
  files: (FileChange & { insertions: number; deletions: number })[]
  insertions: number
  deletions: number
}

/**
 * A session total counts settled turns, never writes from an active run. A
 * session with no runs at all, a captured one, has nothing in flight, so all
 * of it counts.
 */
export function completedTurnMessages(
  transcript: TranscriptMessage[],
): TranscriptMessage[] {
  if (!transcript.some((message) => message.runStatus)) return transcript
  const complete: TranscriptMessage[] = []
  let start = -1
  const flush = (end: number) => {
    if (start < 0) return
    const status = transcript[start]?.runStatus
    if (status === 'completed' || status === 'failed' || status === 'cancelled')
      complete.push(...transcript.slice(start, end))
  }
  for (let i = 0; i < transcript.length; i++) {
    if (!startsTurn(transcript[i])) continue
    flush(i)
    start = i
  }
  flush(transcript.length)
  return complete
}

export function projectRunFileChanges(
  transcript: TranscriptMessage[],
): RunFileChanges[] {
  const runs: RunFileChanges[] = []
  let start = -1
  const flush = (end: number) => {
    const prompt = transcript[start]
    // The card is keyed by its prompt. Without the prompt's id, or before the
    // run settles, there is no turn result to show.
    if (
      !prompt?.entryId ||
      !['completed', 'failed', 'cancelled'].includes(prompt.runStatus ?? '')
    )
      return
    const segment = transcript.slice(start, end)
    const settled = new Set(
      segment.flatMap((message) =>
        message.role === 'tool_result' && message.toolUseId
          ? [message.toolUseId]
          : [],
      ),
    )
    const project = (snapshot: boolean) => {
      const messages = segment.map((message) =>
        message.role === 'assistant'
          ? {
              ...message,
              content: message.content.filter((block) =>
                block.type !== 'tool_use'
                  ? true
                  : settled.has(block.id) &&
                    (block.name === 'runFileChanges') === snapshot,
              ),
            }
          : message,
      )
      const changes = projectSessionChanges(messages)
      const counts = new Map<
        string,
        { insertions: number; deletions: number }
      >()
      for (const edit of changes.edits) {
        const count = counts.get(edit.path) ?? { insertions: 0, deletions: 0 }
        count.insertions += edit.insertions
        count.deletions += edit.deletions
        counts.set(edit.path, count)
      }
      return changes.files.map((file) => ({
        ...file,
        ...(counts.get(file.path) ?? { insertions: 0, deletions: 0 }),
      }))
    }
    const files = new Map<string, RunFileChanges['files'][number]>()
    for (const file of project(false)) files.set(file.path, file)
    // The Git tree includes structured and shell writes. Its net counts replace
    // call estimates for the same path, avoiding double counting.
    for (const file of project(true)) files.set(file.path, file)
    if (!files.size) return
    const listed = [...files.values()]
    runs.push({
      key: prompt.entryId,
      start,
      end,
      waiting: segment.flatMap((message, i) =>
        i > 0 && message.role === 'user' ? [start + i] : [],
      ),
      files: listed,
      insertions: listed.reduce((n, file) => n + file.insertions, 0),
      deletions: listed.reduce((n, file) => n + file.deletions, 0),
    })
  }
  for (let i = 0; i < transcript.length; i++) {
    if (!startsTurn(transcript[i])) continue
    if (start >= 0) flush(i)
    start = i
  }
  if (start >= 0) flush(transcript.length)
  return runs
}

/**
 * Which row each card follows: the last row its own turn created. Keyed by
 * row key. A run whose turn drew no row is left out rather than guessed at.
 */
export function cardsAfterRows(
  rows: readonly { key: string; at?: number }[],
  runs: readonly RunFileChanges[],
): Map<string, RunFileChanges[]> {
  const map = new Map<string, RunFileChanges[]>()
  for (const run of runs) {
    let key: string | undefined
    for (const row of rows) {
      if (row.at === undefined || row.at < run.start) continue
      if (row.at >= run.end) break
      if (!run.waiting.includes(row.at)) key = row.key
    }
    if (key) map.set(key, [...(map.get(key) ?? []), run])
  }
  return map
}
